import React, { useMemo, useState } from 'react'
import { OfferListCommon } from '../../offers/OfferListCommon'
import { TeaserProps } from '../../offers/teaser'

type FirmOfferProps = {
  offers: TeaserProps[]
  color?: string
}

const FirmOfferTemplate: React.FC<FirmOfferProps> = ({
  offers = [],
  color = undefined,
}) => {
  const [locationFilter, setLocationFilter] = useState<string>('')
  const [contractTypeFilter, setContractTypeFilter] = useState<string>('')
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('')

  const options = useMemo(() => {
    const jobTypes = [
      ...new Set(
        (offers?.map((offer) => offer.jobType) || []).filter(
          Boolean,
        ) as string[],
      ),
    ].sort((a, b) => a.localeCompare(b))

    const contractTypes = [
      ...new Set(
        (offers?.map((offer) => offer?.contractType) || []).filter(
          Boolean,
        ) as string[],
      ),
    ].sort((a, b) => a.localeCompare(b))

    const locations = [
      ...new Set(
        (offers?.map((offer) => offer.city) || []).filter(Boolean) as string[],
      ),
    ].sort((a, b) => a.localeCompare(b))

    return {
      jobTypes,
      contractTypes,
      locations,
    }
  }, [offers])

  const filteredOffers = useMemo(
    () =>
      offers?.filter((offer) => {
        const locationMatch =
          locationFilter && offer.city
            ? offer.city.toLowerCase().includes(locationFilter.toLowerCase())
            : true
        const contractTypeMatch =
          contractTypeFilter && offer?.contractType
            ? offer.contractType
                .toLowerCase()
                .includes(contractTypeFilter.toLowerCase())
            : true
        const jobTypeMatch =
          jobTypeFilter && offer.jobType
            ? offer.jobType.toLowerCase().includes(jobTypeFilter.toLowerCase())
            : true

        return locationMatch && contractTypeMatch && jobTypeMatch
      }),
    [offers, locationFilter, contractTypeFilter, jobTypeFilter],
  )

  return (
    <div className="flex flex-col gap-8 mt-3">
      {/* Filtres */}
      {offers.length > 5 && (
        <div className="flex flex-col justify-center gap-2 lg:flex-row">
          {/* Filtre par localisation */}
          <select
            id="locations"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="" className="font-semibold">
              Localisation
            </option>
            {options.locations.map((location) => (
              <option
                key={location}
                value={location}
                aria-label={`localisation : ${location}`}
                className="hover:bg-blue-500 hover:text-white"
              >
                {location}
              </option>
            ))}
          </select>

          {/* Filtre par type de contrat */}
          <select
            id="contractTypes"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            value={contractTypeFilter}
            onChange={(e) => setContractTypeFilter(e.target.value)}
          >
            <option value="" className="font-semibold">
              Type de contrat
            </option>
            {options.contractTypes.map((contractType) => (
              <option
                key={contractType}
                value={contractType}
                aria-label={`localisation : ${contractType}`}
                className="hover:bg-blue-500 hover:text-white"
              >
                {contractType}
              </option>
            ))}
          </select>

          {/* Filtre par type de travail */}
          <select
            id="jobTypes"
            className="bg-gray-50 border border-gray-300 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
          >
            <option value="" className="font-semibold">
              Type de métier
            </option>
            {options.jobTypes.map((jobType) => (
              <option
                key={jobType}
                value={jobType}
                aria-label={`localisation : ${jobType}`}
                className="hover:bg-blue-500 hover:text-white"
              >
                {jobType}
              </option>
            ))}
          </select>
        </div>
      )}

      <OfferListCommon
        offers={filteredOffers?.map((offer) => ({
          ...offer,
        }))}
        color={color}
        emptyMessage={
          offers?.length > 0
            ? 'Aucune offre ne correspond aux filtres sélectionnés.'
            : 'Nous ne recrutons pas actuellement'
        }
      />
    </div>
  )
}

export default FirmOfferTemplate
